import { LicenseInfo } from '@mui/x-license';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { CommonLoader } from './components/atoms/CommonLoader';
import { ConfigRouteProvider } from './providers/ConfigRouteProvider';
import { loadRoutes } from './routes';
import { useConfig } from '@nexelem/config';
import { useAuthContext } from './providers/AuthProvider';
import { useLoadShiftsEffect } from './components/organisms/ProductionPlans/hooks/useKanbanFilterStore';
import { useAPI } from '@nexelem/api';
import { useGraphneAPI } from '@nexelem/graphne-api';

const App = memo(() => {
  const { clientId } = useConfig();
  const authContext = useAuthContext();
  const { ready } = useTranslation();
  const api = useAPI();
  const graphneApi = useGraphneAPI();

  const {
    data: router,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['app-navigation'],
    queryFn: () => loadRoutes(clientId),
    enabled: !!clientId,
  });

  const { browserRouter, routeConfig, defaultPath } = useMemo(() => {
    if (!router) {
      return { browserRouter: undefined, routeConfig: [], defaultPath: '/' };
    }
    return router.getRouter(authContext, api, graphneApi, clientId);
  }, [router, authContext, api, graphneApi]);

  useEffect(() => {
    LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);
  }, []);

  useLoadShiftsEffect();

  if (isLoading || !ready || !browserRouter) {
    return <CommonLoader />;
  }
  if (isError) {
    // TODO
    return <div>Error</div>;
  }
  return (
    <ConfigRouteProvider defaultPath={defaultPath} routeConfig={routeConfig}>
      <RouterProvider router={browserRouter} />
    </ConfigRouteProvider>
  );
});

App.displayName = 'App';

export default App;
